import {
  DHCheckKind,
  DHCommentLevel,
  DHStatus,
  EventCause,
  EventSource,
  MachineType,
  OnHoldReason,
  PastillaName,
  PastillaStatus,
  TSWay,
} from "#imports"

export const $icons = {
  versus: "i-ri-exchange-line",
  internal_external: "i-ri-global-line",
  matching: "i-fa6-solid-down-left-and-up-right-to-center",
  calendar: "i-fa6-solid-calendar-days",
  calendar_view: {
    table: "i-heroicons-table-cells",
    month: "i-fa6-solid-calendar-days",
    timeline: "i-fa6-solid-bars-staggered",
  },
  refresh: "i-fa6-solid-rotate-right",
  meter_change: "i-ri-exchange-funds-line",
  eye: "i-fa6-solid-eye",
  eye_slash: "i-fa6-solid-eye-slash",
  back_arrow: "i-fa6-solid-chevron-left",
  birthday: "i-ri-cake-2-fill",
  note: "i-ri-chat-new-line",
  help: "i-mdi-information-outline",
  edit: "i-heroicons-pencil",
  redo: "i-fa6-solid-arrow-rotate-left",
  warning: "i-fa6-solid-circle-exclamation",
  triangle_warning: "i-fa6-solid-triangle-exclamation",
  check: "i-fa6-solid-check",
  double_check: "i-fa6-solid-check-double",
  bolt: "i-material-symbols-electric-bolt",
  add: "i-fa6-solid-plus",
  back: "i-fa6-solid-arrow-left",
  forward: "i-fa6-solid-arrow-right",
  cross: "i-heroicons-x-mark-20-solid",
  phone: "i-fa6-solid-phone",
  mail: "i-fa6-solid-envelope",
  key: "i-fa6-solid-key",
  upload: "i-fa6-solid-upload",
  play: "i-fa6-solid-play",
  pause: "i-heroicons-pause",
  manual: "i-material-symbols-mouse-outline",
  chart_line: "i-fa6-solid-chart-line",
  paper_plane: "i-fa6-solid-paper-plane",
  info: "i-fa6-solid-circle-info",
  inbox: "i-fa6-solid-inbox",
  timeline: "i-fa6-solid-timeline",
  alarm: "i-fa6-regular-clock",
  processed: "i-heroicons-clipboard-document-check",
  user: "i-fa6-solid-circle-user",
  plug: "i-material-symbols-power-plug-outline",
  electric_meter: "i-material-symbols-electric-meter",
  search: "i-fa6-solid-magnifying-glass",
  pinned: "i-ri-pushpin-fill",
  todo: "i-material-symbols-event-list-outline-sharp",
  event: "i-heroicons-calendar-date-range-16-solid",
  regulation: "i-material-symbols-calculate-outline",
  cancelation: "i-heroicons-receipt-refund",
  database: "i-fa6-solid-database",
  goldensource: "i-material-symbols-stars-outline-rounded",
  commissioning: "i-material-symbols-front-loader-sharp",
  email: "i-fa6-solid-envelope",
  pdf: "i-fa6-solid-file-pdf",
  empty: "i-mdi-set-none",
  cog: "i-heroicons-cog",
  text: "i-heroicons-document-text",
  model: {
    assetowner: "i-fa6-solid-user-group",
    assetmanager: "i-fa6-solid-users-gear",
    counterpart: "i-fa6-solid-building-user",
    holding: "i-fa6-solid-building-columns",
    contract: "i-fa6-solid-file-contract",
    site: "i-material-symbols-location-on-outline",
    portfolio: "i-fa6-solid-wallet",
    invoice: "i-fa6-solid-file-invoice-dollar",
    dso: "i-material-symbols-electric-bolt",
    tso: "i-material-symbols-auto-transmission",
    company: "i-fa6-solid-building",
    person: "i-fa6-solid-circle-user",
    asset: "i-fa6-solid-circle-dot",
    deliverypoint: "i-fa6-solid-plug",
    meter: "i-fa6-solid-plug",
    submeter: "i-fa6-solid-plug",
    headmeter: "i-fa6-solid-plug",
    country: "i-fa6-solid-earth-europe",
    region: "i-fa6-solid-map",
  },
} as const

export const mapModelIconColor = {
  company: "text-gray-500",
  person: "text-violet-500",
  assetowner: "text-rnxorange-300",
  assetmanager: "text-red-300",
  counterpart: "text-purple-400",
  holding: "text-rose-300",
  dso: "text-yellow-500",
  tso: "text-amber-500",
  contract: "text-rnxblue-400",
  invoice: "text-rnxgreen-500",
  country: "text-blue-600",
  region: "text-blue-500",
  portfolio: "text-blue-400",
  site: "text-rnxgreen-400",
  deliverypoint: "text-rnxblue-500",
  meter: "text-rnxblue-500",
  submeter: "text-rnxblue-400",
  headmeter: "text-rnxblue-400",
  asset: "text-rnxorange-400",
} as const

// __________ MachineType __________

const iconPrefix = "/images/google-markers"

export const mapTechnoGMapIcon: Record<MachineType, string> = {
  // wind
  [MachineType.enum.wind_turbine_onshore]: `${iconPrefix}/markers-google-wind-turbine.svg`,
  [MachineType.enum.wind_turbine_offshore]: `${iconPrefix}/markers-google-wind-offshore.svg`,
  // solar
  [MachineType.enum.solar_field_ground_mounted]: `${iconPrefix}/markers-google-solar-panel.svg`,
  [MachineType.enum.solar_field_rooftop]: `${iconPrefix}/markers-google-solar-roof-panel.svg`,
  [MachineType.enum.solar_field_canopy]: `${iconPrefix}/markers-google-solar-canopy-panel.svg`,
  // hydro
  [MachineType.enum.hydro_turbine_reservoir]: `${iconPrefix}/markers-google-dam.svg`,
  [MachineType.enum.hydro_turbine_run_of_river]: `${iconPrefix}/markers-google-hydro.svg`,
  [MachineType.enum.hydro_turbine_pumped_storage]: `${iconPrefix}/markers-google-hydro.svg`,
  // Coge
  [MachineType.enum.cogeneration_biomass]: `${iconPrefix}/markers-google-bio.svg`,
  [MachineType.enum.cogeneration_waste]: `${iconPrefix}/marker-incineration.svg`,
  [MachineType.enum.cogeneration_other]: `${iconPrefix}/marker-coge-other.svg`,
  //Consumption
  [MachineType.enum.consumption_site_industry_retail]:
    `${iconPrefix}/markers-google-consumption.svg`,
  // Battery
  [MachineType.enum.battery_default]: `${iconPrefix}/markers-google-battery.svg`,
}

export const mapMachineTypeIconUrl: Record<MachineType, string> = {
  // wind
  [MachineType.enum.wind_turbine_onshore]: "i-rnx-wind-turbine-onshore",
  [MachineType.enum.wind_turbine_offshore]: "i-rnx-wind-turbine-offshore",
  // solar
  [MachineType.enum.solar_field_canopy]: "i-rnx-solar-canopy",
  [MachineType.enum.solar_field_rooftop]: "i-rnx-solar-rooftop",
  [MachineType.enum.solar_field_ground_mounted]: "i-rnx-solar-plain",
  // hydro
  [MachineType.enum.hydro_turbine_reservoir]: "i-rnx-hydro-dam",
  [MachineType.enum.hydro_turbine_run_of_river]: "i-rnx-hydro-default",
  [MachineType.enum.hydro_turbine_pumped_storage]: "i-rnx-hydro-default",
  // coge
  [MachineType.enum.cogeneration_biomass]: "i-rnx-coge-bio",
  [MachineType.enum.cogeneration_waste]: "i-rnx-coge-incineration",
  [MachineType.enum.cogeneration_other]: "i-rnx-coge-other",
  // consumption
  [MachineType.enum.consumption_site_industry_retail]: "i-rnx-consumption",
  // battery
  [MachineType.enum.battery_default]: "i-material-symbols-battery-charging-full-outline-sharp",
}

// __________ GoogleMap __________

export const gmapDisableUIExceptFullScreen = {
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: true,
  gestureHandling: "cooperative",
} as const

export const gmapDefaultCenter = { lat: 47.0844, lng: 2.3964 } as const // Bourges as a center ref

// __________ Pastilla __________

export const mapPastillaStatusIconColor: Record<PastillaStatus, string> = {
  [PastillaStatus.enum.error]: "text-red-600",
  [PastillaStatus.enum.good]: "text-green-600",
  [PastillaStatus.enum.warning]: "text-yellow-600",
  [PastillaStatus.enum.missing]: "text-gray-500",
  [PastillaStatus.enum.na]: "text-gray-500",
  [PastillaStatus.enum.skipped]: "text-gray-500",
  [PastillaStatus.enum.missing_configuration]: "text-yellow-600",
  [PastillaStatus.enum.compute_error]: "text-red-600",
}

export const mapPastillaNameIconName: Record<PastillaName, string> = {
  [PastillaName.enum.power_production]: "i-material-symbols-bolt-outline-rounded",
  [PastillaName.enum.availability]: "i-material-symbols-event-available",
  [PastillaName.enum.pr]: "i-ri-sun-foggy-line",
  [PastillaName.enum.data_health]: "i-mdi-medication-outline",
}

// __________ Invoice __________

export const mapInvoiceActionIcon = {
  compute: "i-fa6-solid-calculator",
  publish: "i-fa6-solid-file-circle-check",
  submit: "i-fa6-solid-share-from-square",
  //
  download_pdf: "i-fa6-solid-file-pdf",
  download_excel: "i-fa6-solid-file-excel",
  //
  [InvoiceCancelActions.enum.unpublish]: "i-fa6-solid-backward",
  [InvoiceCancelActions.enum.credit_note]: "i-fa6-solid-square-minus",
  [InvoiceCancelActions.enum.delete]: "i-fa6-solid-trash",
  //
  set_as_submitted: "i-fa6-solid-envelope-circle-check",
  set_as_paid: "i-ri-money-euro-circle-line",
  set_as_accounted: "i-ri-loader-2-line",
  set_as_computed: "i-fa6-solid-check",
  set_as_validated: "i-fa6-solid-circle-check",
  //
  set_to_waiting_for_publication: "i-fa6-solid-circle-check",
  unpublish_waiting: "i-fa6-solid-ban",
}

// __________ Notifications __________

export const mapNotifLevelIcon = {
  [NotifLevel.enum.alert]: {
    color: "red",
    icon: "i-fa6-solid-circle-xmark",
  },
  [NotifLevel.enum.warning]: {
    color: "yellow",
    icon: "i-fa6-solid-triangle-exclamation",
  },
  [NotifLevel.enum.info]: {
    color: "rnxblue",
    icon: "i-fa6-solid-circle-info",
  },
  [NotifLevel.enum.success]: {
    color: "rnxgreen",
    icon: "i-fa6-solid-circle-check",
  },
} as const

// __________ DataHealth __________

export const mapDHCheckToIcon: Record<DHCheckKind, string> = {
  [DHCheckKind.enum.config]: "i-material-symbols-settings-outline",
  [DHCheckKind.enum.freshness]: "i-material-symbols-alarm-outline",
  [DHCheckKind.enum.completeness]: "i-material-symbols-incomplete-circle",
  [DHCheckKind.enum.consistency]: $icons.matching,
}

export const mapDHStatusToColors: Record<DHStatus, UIColor> = {
  [DHStatus.enum.good]: "rnxgreen",
  [DHStatus.enum.warning]: "yellow",
  [DHStatus.enum.critical]: "red",
  [DHStatus.enum.missing]: "gray",
}

export const mapCommentLevelTocolor: Record<DHCommentLevel, UIColor> = {
  [DHCommentLevel.enum.error]: "red",
  [DHCommentLevel.enum.info]: "rnxblue",
  [DHCommentLevel.enum.aggregated]: "gray",
}

// __________ OnHoldReason __________

export const mapOnHoldReasonColor: Record<OnHoldReason, UIColor> = {
  [OnHoldReason.enum.missing_iban]: "orange",
  [OnHoldReason.enum.waiting_buyer]: "amber",
  [OnHoldReason.enum.waiting_legal_confirmation]: "red",
  [OnHoldReason.enum.verification_needed]: "yellow",
  [OnHoldReason.enum.incorrect_config]: "red",
  [OnHoldReason.enum.edfoa_producer_does_not_exist]: "rose",
  [OnHoldReason.enum.edfoa_no_rights_on_account]: "pink",
  [OnHoldReason.enum.counter_reset]: "purple",
  [OnHoldReason.enum.other]: "gray",
  [OnHoldReason.enum.waiting_acc_topo]: "gray",
}

// __________ Events __________

export const mapEventSourceColor: Record<EventSource, UIColor> = {
  [EventSource.enum.ai]: "blue",
  [EventSource.enum.power_production]: "rnxblue",
  [EventSource.enum.manual]: "gray",
  [EventSource.enum.alemca]: "red",
  [EventSource.enum.smart_oa]: "rose",
  [EventSource.enum.elmy]: "green",
  [EventSource.enum.tilt_energy]: "amber",
  [EventSource.enum.symphonics]: "violet",
}

export const mapEventCauseColor: Record<EventCause, UIColor> = {
  [EventCause.enum.preventive_maintenance]: "indigo",
  [EventCause.enum.corrective_maintenance]: "rnxblue",
  //
  [EventCause.enum.network]: "emerald",
  [EventCause.enum.weather]: "cyan",
  [EventCause.enum.breakdown]: "red",
  [EventCause.enum.disaster]: "yellow",
  [EventCause.enum.work_handover]: "violet",
  //
  [EventCause.enum.negative_price]: "amber",
  [EventCause.enum.balancing_mechanism]: "rnxorange",
  [EventCause.enum.smart_oa]: "rose",
  [EventCause.enum.intraday]: "teal",
  //
  [EventCause.enum.unknown]: "gray",
}

// __________ Timeseries __________

export const MapTSWayBadgeColor: Record<TSWay, string> = {
  [TSWay.enum.production]: "green",
  [TSWay.enum.consumption]: "orange",
  [TSWay.enum.injection]: "rnxblue",
  [TSWay.enum.autoconsumption]: "amber",
}
